import React, { useState, useCallback, useEffect } from 'react';
import MultiFreezeThumbnails from 'components/freeze/MultiFreezeThumbnails.js';
import {MULTI_FREEZE} from 'constants/track-types.js';
import {MULTI_SYNTH, SAMPLED_INSTRUMENT} from 'constants/instrument-types.js';
import {TRIGGER_SAMPLE, RELEASE_SAMPLE} from 'constants/event-types.js';
import { connect } from "react-redux";
import ConditionedComponent from 'components/ConditionedComponent.js';
import Colors from 'components/Colors.js';

const COLORS = new Colors();
const triggerColor = COLORS.getColor(1);
const DRUM_TAGS = new Set(['clap', 'tom', 'toms', 'clave', 'perc', 'snare', 'kick', 'hi-hat', 'rim', 'side-stick', 'breaks', 'sub', 'ride', 'piano', 'violin']);

function TrackVisualizer(props) {
    let [lastTrigger, setLastTrigger] = useState(0);
    let [lastRelease, setLastRelease] = useState(0);
    let [thumbnailStyle, setThumbnailStyle] = useState({});
    let [additionalStyle, setAdditionalStyle] = useState({});

    const onClickMute = useCallback(() => {
        props.track.mute(!props.track.muted);
    }, [props.track]);

    const onTrigger = useCallback((e) => {
        if (e.trackNumber === props.track.trackNumber) {
            setTimeout(() => {
                setLastTrigger(new Date().getTime() + e.duration + 50);
                let brightness = 1 - e.sliceStartTime/e.sliceDuration;
                let style = {... additionalStyle};
                style['borderBottomColor'] = COLORS.brighten(triggerColor, brightness);
                setAdditionalStyle(style);
                setTimeout(onRelease, e.duration * 1000);
            }, e.time);
        }
    }, [props.track, props.currentSubSequenceId, additionalStyle]);

    const onRelease = useCallback((e) => {
        if (e === undefined ||
            e.trackNumber === props.track.trackNumber) {
            setLastRelease(new Date().getTime());
        }
    }, [props.track]);

    const addEventListeners = () => {
        window.addEventListener(TRIGGER_SAMPLE, onTrigger);
        window.addEventListener(RELEASE_SAMPLE, onRelease);
    };

    const removeEventListeners = () => {
        window.removeEventListener(TRIGGER_SAMPLE, onTrigger);
        window.removeEventListener(RELEASE_SAMPLE, onRelease);
    };

    useEffect(() => {
        addEventListeners();
        return removeEventListeners;
    }, []);

    let sample =     props.track.sample;
    let thumbnail = sample === undefined ? undefined : sample.getThumbnail();

    useEffect(() => {
        let style = {};
        let color = undefined;

        if ( thumbnail === undefined || thumbnail === '') {
            let stringToConvert = sample && sample.getId() !== undefined ? 
                sample.getId() :
                props.track.getName();
            color = COLORS.getColorForHash(stringToConvert);
            style.backgroundColor = color;
        }
        
        if (props.muted) {
            style['opacity'] = 0.2;
        } else {
            style['opacity'] = 1;
        }

        setThumbnailStyle(style);
    }, [props.track, props.muted]);

    let className = "track-visualizer";
    let triggering = lastTrigger > lastRelease;
    if (lastTrigger > lastRelease) {
        className += ' triggering';
    }

    let track = props.track;
    let innerText = undefined;
    if (sample && sample.tags) {
        innerText = sample.tags.find(tag => DRUM_TAGS.has(tag));
        if (!innerText) {
            innerText = sample.tags[0];
        }
    }
    if (track && track.getInstrumentType() == MULTI_SYNTH) {
        innerText = 'Synth';
    }
    if (track && track.getInstrumentType() === SAMPLED_INSTRUMENT) {
        let samples = track.instrument.getSamples();
        if (samples[0]) {
            innerText = samples[0].tags.find(tag => DRUM_TAGS.has(tag));
        }
    }

    let innerComponent = '';
    if (track && track.getTrackType() === MULTI_FREEZE) {
        innerComponent = (<MultiFreezeThumbnails track={track}/>)
        className += " is-multi ";
    }

    return (
        <div
          onClick={() => onClickMute()}
          style={{... thumbnailStyle, ... additionalStyle}} className={className}>
          <ConditionedComponent condition={thumbnail !== undefined} >
            <img src={thumbnail}/>
          </ConditionedComponent>
          {!thumbnail && innerText ? <span className="inner-text">{innerText}</span> : ''}
          {innerComponent}
        </div>
    );
}

const mapStateToProps = (state, props) => {
    return {
        muted: props.track.muted
    };
};

export default connect(mapStateToProps, null)(TrackVisualizer);

